import { API_ENDPOINTS } from '../config/api';

import { getAuth } from 'firebase/auth';

// Helper function to get authentication headers
async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      return {};
    }
    
    const token = await user.getIdToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  } catch (error) {
    console.error('Error getting auth headers:', error);
    return {};
  }
}

export interface FileUploadResponse {
  uploadUrl: string;
  downloadUrl: string;
  fileName: string;
  originalName: string;
  fileType: string;
  expiresAt: string;
}

export interface FileMetadata {
  fileName: string;
  downloadUrl: string;
  contentType: string;
  size: number;
  created: string;
  updated: string;
  expiresAt: string;
}

export interface UploadedFile {
  fileName: string;
  originalName: string;
  fileType: string;
  downloadUrl: string;
  size: number;
  expiresAt: string;
}

// File validation constants
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const ALLOWED_FILE_TYPES = [
  // Images
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
  // Documents
  'application/pdf'
];

export function validateFile(file: File): string[] {
  const errors: string[] = [];

  if (!file) {
    errors.push('No file provided');
    return errors;
  }

  if (file.size > MAX_FILE_SIZE) {
    errors.push(`File size too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`);
  }

  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    errors.push(`File type ${file.type} not allowed`);
  }

  return errors;
}

export function getFileIcon(fileType: string): string {
  if (fileType.startsWith('image/')) return '🖼️';
  if (fileType === 'application/pdf') return '📄';
  if (fileType.includes('word')) return '📝';
  if (fileType.includes('excel') || fileType.includes('sheet')) return '📊';
  if (fileType.includes('powerpoint') || fileType.includes('presentation')) return '📋';
  if (fileType.startsWith('text/')) return '📃';
  if (fileType === 'application/json') return '⚙️';
  return '📎';
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export async function getUploadUrl(file: File): Promise<FileUploadResponse> {
  
  
  const response = await fetch(API_ENDPOINTS.FILES_UPLOAD_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getAuthHeaders()),
    },
    body: JSON.stringify({
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    }),
  });

  

  if (!response.ok) {
    const error = await response.json();
    console.error('❌ API: Failed to get upload URL:', error);
    throw new Error(error.error || 'Failed to get upload URL');
  }

  const result = await response.json();
  
  return result;
}

export async function uploadFileToGCS(file: File, uploadUrl: string): Promise<void> {
  
  
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: file,
  });

  

  if (!response.ok) {
    console.error('❌ GCS: Upload failed with status:', response.status);
    throw new Error('Failed to upload file to Google Cloud Storage');
  }
  
  
}

export async function uploadFile(file: File, onProgress?: (progress: number) => void): Promise<UploadedFile> {
  // Validate file
  const errors = validateFile(file);
  if (errors.length > 0) {
    throw new Error(errors.join(', '));
  }

  try {
    // Step 1: Get signed upload URL
    onProgress?.(10);
    const uploadResponse = await getUploadUrl(file);
    
    // Step 2: Upload file to GCS
    onProgress?.(30);
    await uploadFileToGCS(file, uploadResponse.uploadUrl);
    
    onProgress?.(100);
    
    return {
      fileName: uploadResponse.fileName,
      originalName: uploadResponse.originalName,
      fileType: uploadResponse.fileType,
      downloadUrl: uploadResponse.downloadUrl,
      size: file.size,
      expiresAt: uploadResponse.expiresAt,
    };
  } catch (error) {
    throw new Error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

export async function getFileMetadata(fileName: string): Promise<FileMetadata> {
  const response = await fetch(`${API_ENDPOINTS.FILES_GET}/${fileName}`, {
    headers: await getAuthHeaders(),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get file metadata');
  }

  return response.json();
}

export async function deleteFile(fileName: string): Promise<void> {
  const response = await fetch(`${API_ENDPOINTS.FILES_DELETE}/${fileName}`, {
    method: 'DELETE',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete file');
  }
}

export async function listFiles(): Promise<{ files: any[] }> {
  const response = await fetch(API_ENDPOINTS.FILES_LIST, {
    headers: await getAuthHeaders(),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to list files');
  }

  return response.json();
}
